// Full-org patient roster snapshot for offline mode (spec §2). The merge is
// the SAME logic usePatientDirectory applies live — extracted so the online
// directory and the offline cache can never drift.
//
// Task 7 Step 1 verify finding — listPatient("") limit behavior:
// `api.listPatient(text, limit = 300)` (src/services/api.js) posts to the
// "Search Patients" backend process (backend/processes/assistant-v2/js/Search
// Patients_component2648134.js, lucasai-backend). That script hard-clamps the
// trigger's `limit` param itself: `if (limit > 10000) limit = 10000;` — no
// argument can push the response past 10,000 rows. For a name-only/empty-text
// search (our case: filter === "") `dobActive` is false, so
// `organization.getUsersWhere(baseExpression, limit)` runs directly against
// that clamped limit; the guide (backend-guides/2026-07-13-search-patients-
// scale-and-dob-shapes.md) confirms "the response stays capped at `limit`".
// So this is a HARD server-side cap at 10,000 that no caller can lift —
// passing `api.listPatient("", 10000)` gets the maximum available. The same
// guide records premiumhealthcare alone at 43,869 patients (2026-07-13
// measurement), so orgs above ~10k patients will get a partial offline
// roster snapshot (an arbitrary ~10k-row subset per `getUsersWhere`'s
// unordered scan, not necessarily the "first" 10k in any meaningful order).
// Per the Task 7 brief this is reported as a concern (DONE_WITH_CONCERNS),
// not fixed here — raising the backend cap is a separate, deliberate change.
import api from "../services/api";
import { dobForDisplay } from "./chatSearch";
import { normalizeClinicalProfileRow, normalizePatientAccountRow } from "./peptidePatient";
import {
  buildScope,
  getCacheEpoch,
  isCacheFresh,
  loadPatientRosterMeta,
  savePatientRoster,
  ROSTER_MAX_AGE_MS,
} from "./offlineCache";

// Largest limit the "Search Patients" backend process honors (see header
// comment) — passing anything higher has no further effect.
const ROSTER_SNAPSHOT_LIMIT = 10000;

const normalizeName = (value) => (value || "").trim();
const shouldHideName = (value) => {
  const name = normalizeName(value);
  if (!name) return false;
  return /\btest\b/i.test(name) || /\d/.test(name) || /^[a-z]$/i.test(name);
};

export const mergePatientDirectoryRows = (rawUsers, rawProfiles, filter) => {
  const users = (rawUsers || [])
    .filter((user) => {
      const lastName = normalizeName(user?.lastName);
      const firstName = normalizeName(user?.firstName);
      if (!user?.dob) return false;
      if (lastName.toLowerCase() === "anonymous") return false;
      return !shouldHideName(firstName) && !shouldHideName(lastName);
    })
    .map((user) => {
      const normalized = normalizePatientAccountRow(user);
      if (!normalized) return null;
      return { ...user, ...normalized, dob: dobForDisplay(user.dob) };
    })
    .filter(Boolean);

  const cpProfiles = (rawProfiles || [])
    .map((profile) => {
      const sourceRow = { ...profile, mobilePhone: profile.phone || "" };
      const normalized = normalizeClinicalProfileRow(sourceRow);
      if (!normalized) return null;
      return { ...sourceRow, ...normalized, dob: dobForDisplay(profile.dob) };
    })
    .filter(Boolean);

  const existingEmails = new Set(users.filter((u) => u.email).map((u) => u.email.toLowerCase()));
  for (const cp of cpProfiles) {
    if (cp.email && existingEmails.has(cp.email.toLowerCase())) continue;
    if (filter) {
      const search = filter.toLowerCase();
      const name = `${cp.firstName || ""} ${cp.lastName || ""}`.toLowerCase();
      if (!name.includes(search) && !(cp.email || "").toLowerCase().includes(search)) continue;
    }
    users.push(cp);
  }
  return users;
};

// PHI allowlist for a roster row on its way into device storage (audit BS1-FE).
//
// mergePatientDirectoryRows spreads the RAW backend vertex into each row
// (`{ ...user, ...normalized }`), and normalizeSelectablePatient additionally
// re-attaches the whole record as `raw`. For the live directory that is just a
// fat object in memory; writing it to IndexedDB is different — it means the
// entire user vertex, plus every field anyone adds to it later, lands on the
// device with nobody having decided that it should. The org's whole roster is
// up to 10,000 of those.
//
// So the cache stores a NAMED projection instead. Each field below is here
// because a real consumer of a CACHED row reads it — a missed field breaks an
// offline picker, so the list is derived from actual reads, not from a guess at
// the shape:
//
//   id ................. every consumer: react keys, /patients/:id routing,
//                        api.getPatient/getClinicalProfile, the picker's
//                        selection key (ModalNewChat mapPatientRow)
//   source ............. ModalNewChat drops clinical-profile rows from the
//                        encounter picker; Patients + PatientPanelList pick the
//                        status pill; frozenPatientIdentityForRow requires it
//   patientUserId /
//   clinicalProfileId .. frozenPatientIdentityForRow (peptides patient panel)
//                        checks these as OWN keys, so both are always emitted,
//                        null included
//   firstName/lastName . every list, and patientMatchesSearch
//   name ............... PatientSearchSelect's fullName fallback
//   dob ................ displayed everywhere, and a search token type
//   email .............. searchCachedPatientRows' email match;
//                        ClinicalProfileDetail's profile matching
//   mobilePhone ........ Patients table column, PatientPanelList meta line,
//                        ClinicalProfileDetail demographics
//   phone .............. the normalized row's own phone field
//   gender ............. ClinicalProfileDetail demographics (sex)
//   custom.gender ...... Patients table gender column (ONLY this one key of
//                        `custom` is read; the rest of it never leaves online)
//   status ............. Patients + PatientPanelList status pill,
//                        ClinicalProfileDetail
//   address/city/
//   state/zip .......... the normalized directory-row contract, kept so an
//                        offline row is shape-identical to an online one
//   clinicalProfile /
//   profile ............ ClinicalProfileDetail's Path 3 ("fall back to flat
//                        data from navigation state"), which is THE offline
//                        path: both API lookups above it fail with no network.
//                        Deliberate and reviewed — dropping it would degrade
//                        offline intake review to demographics only.
//
// Deliberately NOT cached: `raw` (the whole vertex, the actual leak), `groups`
// (no cached-row consumer reads it — the duplicate-merge screen that reads
// groups runs its own live listPatient), and every unknown vertex field.
const ROSTER_ROW_FIELDS = [
  "id",
  "source",
  "patientUserId",
  "clinicalProfileId",
  "firstName",
  "lastName",
  "name",
  "dob",
  "email",
  "mobilePhone",
  "phone",
  "gender",
  "status",
  "address",
  "city",
  "state",
  "zip",
  "clinicalProfile",
  "profile",
];

export const projectRosterRowForCache = (row) => {
  const projected = {};
  ROSTER_ROW_FIELDS.forEach((field) => {
    if (row?.[field] !== undefined) projected[field] = row[field];
  });
  // Always own keys, even when null: frozenPatientIdentityForRow fails closed
  // on a row that merely lacks them.
  projected.patientUserId = row?.patientUserId ?? null;
  projected.clinicalProfileId = row?.clinicalProfileId ?? null;
  if (row?.custom?.gender !== undefined) projected.custom = { gender: row.custom.gender };
  return projected;
};

// Refresh the cached roster at most once per ROSTER_MAX_AGE_MS. Never throws:
// offline caching is best-effort and must not disturb login.
//
// The cache generation is captured BEFORE the network phase, not at
// savePatientRoster entry. This is the largest PHI write in the product (up to
// 10,000 patient rows) and it sits behind a multi-second listPatient +
// listClinicalProfiles window that is entered from both login and outage
// recovery. A logout inside that window must discard the whole snapshot rather
// than re-materialize the departed user's entire roster.
export const snapshotPatientRosterIfStale = async ({ userId, orgId }) => {
  const scope = buildScope(userId, orgId);
  if (!scope) return "skipped";
  const epoch = getCacheEpoch();
  try {
    // Meta only — a freshness check must not deserialize the whole roster
    // (up to 10,000 rows) just to read one timestamp.
    const existing = await loadPatientRosterMeta(scope);
    if (existing && isCacheFresh(existing.fetchedAt, ROSTER_MAX_AGE_MS)) return "fresh";
    const res = await api.listPatient("", ROSTER_SNAPSHOT_LIMIT);
    let profiles = [];
    try {
      const cpRes = await api.listClinicalProfiles();
      profiles = cpRes?.data?.output?.profiles || [];
    } catch {
      // secondary list — roster still worth caching without it
    }
    const merged = mergePatientDirectoryRows(res?.data?.output?.users || [], profiles, "");
    // Project BEFORE the write: what reaches device storage is the reviewed
    // allowlist, not the raw vertex the merge carries around in memory.
    const rows = merged.map(projectRosterRowForCache);
    const saved = await savePatientRoster(scope, rows, Date.now(), { epoch });
    if (!saved) {
      console.log("Offline roster snapshot dropped (cache purged mid-fetch)");
      return "skipped";
    }
    console.log(`Offline roster cached: ${rows.length} patients`);
    return "updated";
  } catch (e) {
    console.warn("Roster snapshot failed:", e?.message);
    return "skipped";
  }
};
